import * as React from 'react';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMenuSelection } from 'global/MenuSelectionContext';
import { useDynamicLogo } from 'hooks/useDynamicLogo';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import { useSideBar } from 'hooks/useSideBar';
import { LanguageSwitcher } from 'components/LanguageSwitcher';
import { cn } from 'lib/utils';
import { useTheme } from 'providers/ThemeProvider';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

interface HeaderProps {
  activeSubmenu?: string;
}

const Header: React.FC<HeaderProps> = ({ activeSubmenu }) => {
  const { t } = useTranslation('common');
  const { theme, isDark } = useTheme();
  const location = useLocation();
  const { logo, isLoading: logoLoading } = useDynamicLogo();
  const { open, titleMenu } = useSideBar();
  const { menuTitle, updateMenuTitle } = useMenuTitle();
  const { secondLevelMenu, firstLevelMenu } = useMenuSelection();
  const [bgImage, setBgImage] = useState<string | null>(null);
  const navigate = useNavigate();

  // Lazy load header background image - does not block initial render
  // TODO: Quando backend entregar dois backgrounds, usar isDark para escolher:
  // - isDark ? headerBackgroundDark : headerBackgroundLight
  useEffect(() => {
    let isMounted = true;
    import('assets/images/header/header-background.png')
      .then(module => {
        if (isMounted) {
          // Preload the image to ensure smooth transition
          const img = new Image();
          img.onload = () => {
            if (isMounted) setBgImage(module.default);
          };
          img.src = module.default;
        }
      })
      .catch(() => {
        // Fallback: keep gradient if image fails to load
      });
    return () => {
      isMounted = false;
    };
  }, [isDark]); // Re-run when theme changes (for future dual backgrounds)

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  useEffect(() => {
    let newTitle: string | boolean = menuTitle;

    // If we have a second level menu selected, use its title
    if (secondLevelMenu) {
      newTitle = secondLevelMenu.label;
    }
    // If we have a first level menu selected but no second level, use the first level title
    else if (firstLevelMenu) {
      newTitle = firstLevelMenu.label;
    }
    // Fallback to path-based titles
    else {
      switch (true) {
        case location.pathname.includes('/acquisitions/signature'):
          newTitle = t('navigation.acquisitions');
          break;
        case location.pathname.includes('/settings/profile'):
          newTitle = t('navigation.settings');
          break;
        case location.pathname.includes('/application-management/team'):
          newTitle = t('navigation.advisorPortfolio');
          break;
        default:
          newTitle = '';
          break;
      }
    }

    if (typeof newTitle === 'string') {
      updateMenuTitle(newTitle);
    }
  }, [location.pathname, titleMenu, updateMenuTitle, menuTitle, secondLevelMenu, firstLevelMenu]);

  if (!theme) return null;

  return (
    <>
      <header
        className={cn(
          'h-20 flex items-center justify-between bg-gradient-to-r from-brand-dark-bg to-brand-darker-bg transition-[padding-left] duration-300',
          isMobile || isTablet ? 'px-4' : 'pl-60 pr-6'
        )}
        style={{
          backgroundImage: bgImage ? `url(${bgImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transition: 'background-image 0.3s ease-in-out',
        }}
      >
        {isMobile || isTablet ? (
          <div className="flex justify-between items-center w-full">
            {!logoLoading && (
              <img
                src={logo || undefined}
                alt="Logo"
                className="max-h-[4.6rem] cursor-pointer"
                onClick={() => navigate('/')}
              />
            )}
            <div className="flex items-center gap-2">
              <h1 className="text-h3 font-semibold text-header-title">{menuTitle || ''}</h1>
              <LanguageSwitcher />
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-h3 font-semibold text-header-title pl-6">{menuTitle || ''}</h1>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              {!logoLoading && <img src={logo || undefined} alt="Logo" className="max-h-[4.6rem]" />}
            </div>
          </>
        )}
      </header>
    </>
  );
};

export default Header;
